import React, { useEffect, useMemo, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import SearchIcon from '@mui/icons-material/Search'
import CircularProgress from '@mui/material/CircularProgress'
import classnames from 'classnames'
import { useDebounce } from '@uidotdev/usehooks'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'
import { useUserContext } from '../../../contexts/UserContext'
import { usePersonDetailStore } from '../stores/personDetailStore'
import CustomInfiniteScroll from '../../../components/CustomInfiniteScroll'
import { QuoteCard } from './QuoteCard'
import './AttachQuotesModal.scss'

const num = (v) => {
    if (v === null || v === undefined || v === '') return null
    const n = Number(String(v).replace(/,/g, ''))
    return Number.isNaN(n) ? null : n
}

// A /deals row (DealSerializer) reshaped to the QuoteSummary fields QuoteCard
// renders — same mapping the backend's QuoteSummarySerializer does for
// quotes_detail, done client-side because /deals returns the full deal.
const toQuoteSummary = (d) => {
    const car = d.car_detail || {}
    const fq = d.formatted_quote || {}
    return {
        id: d.id,
        description: [car.year, car.make, car.model].filter(Boolean).join(' ')
            + (car.is_new === false ? ' [Used]' : ''),
        spec: car.trim || '',
        lender: fq.lender_name || '',
        note: d.description || '',
        deal_type: d.is_purchase && d.is_one_pay ? 'CASH' : d.is_purchase ? 'FINANCE' : 'LEASE',
        msrp: num(d.msrp),
        due: num(fq.amount_due_at_signing),
        payment: num(fq.monthly_payment),
        term: d.term,
        miles: d.mileage,
        profit: num(d.profit),
        // Carried so the card can mark an expired program — without it every
        // card reads "Active", including the ones the Expired filter selected.
        expiration_date: fq.expiration_date,
    }
}

// ── Filters, mirroring the /quotes page (ListDealsPageHeader/ListDealsList)
//    but with modal-local state — the page's URL-synced store must not be
//    touched from here. Same defaults, same query-param mapping. ──
const DEFAULT_FILTERS = {
    status: 'All', dealType: 'All', year: 'All', make: 'All', model: 'All',
    trim: 'All', users: 'All', monthlyMin: '', monthlyMax: '', msrpMin: '', msrpMax: '',
}
const STATUS_OPTIONS = ['All', 'Active', 'Expired']
const DEAL_TYPE_OPTIONS = ['All', 'Lease', 'Finance', 'Cash']
const yearOptions = () => {
    const y = new Date().getFullYear()
    return ['All', String(y + 1), String(y), String(y - 1)]
}
const yearOrAll = (year) => (year && year !== 'All') ? year : 'all'
const toOptionStrings = (rawList) =>
    (rawList || []).map((item) => item?.cleaned_name || item?.name).filter(Boolean).sort()

const buildUserOptions = (accountUsers) => {
    const labels = (accountUsers ?? []).map((u) => {
        if (typeof u === 'string') return u
        if (!u) return ''
        return u.name || u.full_name
            || [u.first_name, u.last_name].filter(Boolean).join(' ')
            || u.email || String(u.id ?? '')
    }).filter(Boolean)
    return ['All', ...labels]
}

// Same key mapping ListDealsList.buildParams uses (type is lowercased,
// defaults are omitted).
const buildParams = (page, search, f) => {
    const params = new URLSearchParams({ page: String(page) })
    if (search) params.set('s', search)
    const structured = {
        status: f.status,
        type: f.dealType && f.dealType !== 'All' ? f.dealType.toLowerCase() : f.dealType,
        year: f.year,
        make: f.make,
        model: f.model,
        trim: f.trim,
        users: f.users,
        monthly_min: f.monthlyMin,
        monthly_max: f.monthlyMax,
        msrp_min: f.msrpMin,
        msrp_max: f.msrpMax,
    }
    for (const [k, v] of Object.entries(structured)) {
        if (v && v !== 'All') params.append(k, v)
    }
    return params.toString()
}

// ── Dropdowns, visually after the /quotes filter bar but namespaced for the
//    dialog portal (no #ListDealsPage ancestor there). ──
const AqDropdown = ({ label, value, options, onChange, isOpen, onToggle, disabled = false, loading = false, multi = false }) => {
    const ref = useRef(null)
    useEffect(() => {
        if (!isOpen) return
        const onDocMouseDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onToggle(false)
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [isOpen, onToggle])

    const selected = multi
        ? ((!value || value === 'All') ? [] : String(value).split(','))
        : null
    const display = !multi ? value
        : selected.length === 0 ? 'All'
        : selected.length === 1 ? selected[0]
        : `${selected.length} selected`
    const items = multi ? options.filter((o) => o !== 'All') : options

    const pick = (opt) => {
        if (!multi) { onChange(opt); onToggle(false); return }
        if (opt === 'All') { onChange('All'); return }
        const next = selected.includes(opt)
            ? selected.filter((s) => s !== opt)
            : [...selected, opt]
        onChange(next.length ? next.join(',') : 'All')
    }

    return (
        <div className="aq-dropdown" ref={ref}>
            <button
                type="button"
                className={classnames('aq-dropdown-btn', { open: isOpen, disabled })}
                disabled={disabled}
                onClick={() => { if (!disabled) onToggle(!isOpen) }}
            >
                <span className="aq-dropdown-label">
                    {label}: <span className="aq-dropdown-value">{display}</span>
                </span>
                {loading
                    ? <CircularProgress className="aq-spinner" size={11} thickness={5} />
                    : <svg className="aq-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none">
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>}
            </button>
            {isOpen && (
                <div className="aq-dropdown-menu">
                    {multi && (
                        <div
                            className={classnames('aq-dropdown-item multi', { selected: selected.length === 0 })}
                            onClick={(e) => { e.stopPropagation(); pick('All') }}
                        >
                            <span className="aq-multi-check" />All
                        </div>
                    )}
                    {items.map((opt) => (
                        <div
                            key={opt}
                            className={classnames('aq-dropdown-item', {
                                multi,
                                selected: multi ? selected.includes(opt) : opt === value,
                            })}
                            onClick={(e) => { e.stopPropagation(); pick(opt) }}
                        >
                            {multi && <span className="aq-multi-check" />}
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

const AqRange = ({ label, min, max, onMinChange, onMaxChange }) => (
    <div className="aq-range">
        <span className="aq-range-label">{label}</span>
        <span className="aq-dollar">$</span>
        <input type="number" placeholder="min" value={min} onChange={(e) => onMinChange(e.target.value)} />
        <span className="aq-dash">–</span>
        <span className="aq-dollar">$</span>
        <input type="number" placeholder="max" value={max} onChange={(e) => onMaxChange(e.target.value)} />
    </div>
)

// Stale-response guards for the cascading option fetches (same trick as
// listDealsStore). Module-level is fine — one modal is open at a time.
let _makesSeq = 0
let _modelsSeq = 0
let _trimsSeq = 0

// Pick existing quotes to attach to one opportunity. GET /deals only returns
// unattached, account-scoped quotes, so everything listed is attachable.
// Full /quotes filter set, all modal-local. Attach is REAL TIME: checking a
// card attaches immediately (the backend stores a clone) and unchecking
// deletes that clone — we learn each clone's id by diffing the attach
// response's quote ids against the ids known so far (seeded from the
// opportunity prop). Card-body clicks preview the quote in DealModal.
export const AttachQuotesModal = ({ open, onClose, opportunity }) => {
    const attachQuote = usePersonDetailStore((s) => s.attachQuote)
    const detachQuote = usePersonDetailStore((s) => s.detachQuote)
    const { profileState } = useUserContext()
    const userOptions = buildUserOptions(profileState?.account_users)

    const [deals, setDeals] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    const [shelfOpen, setShelfOpen] = useState(false)
    const [openDropdown, setOpenDropdown] = useState(null)
    const [makeOptions, setMakeOptions] = useState(['All'])
    const [modelOptions, setModelOptions] = useState(['All'])
    const [trimOptions, setTrimOptions] = useState(['All'])
    const [loadingOpts, setLoadingOpts] = useState({ makes: false, models: false, trims: false })
    // Source deal id → the clone id its attach created (checked = present).
    const [attachedMap, setAttachedMap] = useState({})
    const [pendingIds, setPendingIds] = useState([])
    const knownQuoteIdsRef = useRef(new Set(opportunity?.quotes || []))

    const setFilter = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value }))
    const toggleDropdown = (id) => (isOpen) => setOpenDropdown(isOpen ? id : null)
    const clearFilters = () => { setFilters(DEFAULT_FILTERS); setSearch('') }
    const hasActiveFilters = search !== ''
        || Object.keys(DEFAULT_FILTERS).some((k) => filters[k] !== DEFAULT_FILTERS[k])

    // One debounced object covering search + every filter, mirroring
    // ListDealsList's refetch-on-filter-change behavior.
    const query = useMemo(() => ({ search, ...filters }), [search, filters])
    const debouncedQuery = useDebounce(query, 300)

    // Page-1 fetches bump the seq, so a next page still in flight for the old
    // filters is dropped rather than appended.
    const fetchSeqRef = useRef(0)
    const loadingMoreRef = useRef(false)

    const fetchPage = async (pageNum, q, append) => {
        const mySeq = append ? fetchSeqRef.current : ++fetchSeqRef.current
        if (!append) {
            setLoading(true)
            loadingMoreRef.current = false
        }
        setLoadError(false)
        try {
            const res = await fetcher.getDeals(buildParams(pageNum, q.search, q))
            if (mySeq !== fetchSeqRef.current) return
            const { results = [], has_more = false, current_page = pageNum } = res?.data || {}
            setDeals((prev) => (append ? [...prev, ...results] : results))
            setHasMore(has_more)
            setPage(current_page)
        } catch (e) {
            if (mySeq === fetchSeqRef.current) setLoadError(true)
        } finally {
            if (mySeq === fetchSeqRef.current) {
                setLoading(false)
                if (append) loadingMoreRef.current = false
            }
        }
    }

    const loadNextPage = () => {
        if (loadingMoreRef.current) return
        loadingMoreRef.current = true
        fetchPage(page + 1, debouncedQuery, true)
    }

    // ≤720px the grid area is overflow: visible and .modal-body scrolls.
    const gridAreaRef = useRef(null)
    const getScrollParent = () => {
        const area = gridAreaRef.current
        return area && getComputedStyle(area).overflowY === 'visible' ? area.parentNode : area
    }

    useEffect(() => {
        if (open) fetchPage(1, debouncedQuery, false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, debouncedQuery])

    // ── Cascading option fetches (Year → Make → Model → Trim), following
    //    ListDealsPageHeader: refetch makes when year changes; reset children
    //    when their parent changes. ──
    useEffect(() => {
        if (!open) return
        const mySeq = ++_makesSeq
        setLoadingOpts((o) => ({ ...o, makes: true }))
        fetcher.makesForYear(false)
            .then((res) => {
                if (mySeq !== _makesSeq) return
                setMakeOptions(['All', ...toOptionStrings(res?.data)])
            })
            .catch(() => {})
            .finally(() => { if (mySeq === _makesSeq) setLoadingOpts((o) => ({ ...o, makes: false })) })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, filters.year])

    const prevMakeRef = useRef(filters.make)
    useEffect(() => {
        if (!open) return
        if (prevMakeRef.current !== filters.make) {
            setFilters((prev) => ({ ...prev, model: 'All', trim: 'All' }))
            prevMakeRef.current = filters.make
        }
        if (!filters.make || filters.make === 'All') {
            ++_modelsSeq
            setModelOptions(['All'])
            return
        }
        const mySeq = ++_modelsSeq
        setLoadingOpts((o) => ({ ...o, models: true }))
        fetcher.modelsForMake(yearOrAll(filters.year), filters.make, false)
            .then((res) => {
                if (mySeq !== _modelsSeq) return
                setModelOptions(['All', ...toOptionStrings(res?.data)])
            })
            .catch(() => {})
            .finally(() => { if (mySeq === _modelsSeq) setLoadingOpts((o) => ({ ...o, models: false })) })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, filters.make, filters.year])

    const prevModelRef = useRef(filters.model)
    useEffect(() => {
        if (!open) return
        if (prevModelRef.current !== filters.model) {
            setFilters((prev) => (prev.trim === 'All' ? prev : { ...prev, trim: 'All' }))
            prevModelRef.current = filters.model
        }
        if (!filters.make || filters.make === 'All' || !filters.model || filters.model === 'All') {
            ++_trimsSeq
            setTrimOptions(['All'])
            return
        }
        const mySeq = ++_trimsSeq
        setLoadingOpts((o) => ({ ...o, trims: true }))
        fetcher.trimsForModel(yearOrAll(filters.year), filters.make, filters.model, false)
            .then((res) => {
                if (mySeq !== _trimsSeq) return
                setTrimOptions(['All', ...toOptionStrings(res?.data)])
            })
            .catch(() => {})
            .finally(() => { if (mySeq === _trimsSeq) setLoadingOpts((o) => ({ ...o, trims: false })) })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, filters.make, filters.model, filters.year])

    // Check → attach now; uncheck → delete the clone now. One in-flight
    // request per card (pendingIds locks its checkbox meanwhile).
    const toggle = async (dealId) => {
        if (pendingIds.includes(dealId)) return
        setPendingIds((prev) => [...prev, dealId])
        try {
            const cloneId = attachedMap[dealId]
            if (cloneId != null) {
                await detachQuote(cloneId)
                knownQuoteIdsRef.current.delete(cloneId)
                setAttachedMap((prev) => {
                    const next = { ...prev }
                    delete next[dealId]
                    return next
                })
                toast.success('Quote removed from the lead.')
            } else {
                const data = await attachQuote(opportunity.id, dealId)
                const ids = data?.quotes || []
                const newCloneId = ids.find((id) => !knownQuoteIdsRef.current.has(id))
                ids.forEach((id) => knownQuoteIdsRef.current.add(id))
                setAttachedMap((prev) => ({ ...prev, [dealId]: newCloneId ?? null }))
                toast.success('Quote attached to the lead.')
            }
        } catch (e) {
            /* store toasts the error; checkbox state simply stays as it was */
        } finally {
            setPendingIds((prev) => prev.filter((id) => id !== dealId))
        }
    }

    // Attached quotes only ever come from loaded cards, so the loaded pages
    // fully resolve the rail's entries.
    const selectedDeals = deals.filter((d) => attachedMap[d.id] !== undefined)

    const emptyText = hasActiveFilters
        ? 'No quotes match your filters.'
        : 'No quotes available to attach — quotes already on a lead are not listed.'

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ className: 'attach-quotes-modal' }}>
            <div className="modal-head">
                <div className="modal-head-ic"><PlaylistAddCheckIcon /></div>
                <div>
                    <div className="modal-title">Attach Quotes</div>
                    <div className="modal-sub">
                        Select existing quotes to attach to this lead. Attaching adds a copy — the
                        original stays in your Quotes list.
                    </div>
                </div>
                <button className="modal-x" onClick={onClose} aria-label="Close">✕</button>
            </div>

            {/* ── Filter bar: Users | Clear | Filters toggle | Search ── */}
            <div className="aq-filter-bar">
                <AqDropdown
                    multi label="Users" value={filters.users} options={userOptions}
                    onChange={setFilter('users')}
                    isOpen={openDropdown === 'users'} onToggle={toggleDropdown('users')}
                />
                {hasActiveFilters && (
                    <button type="button" className="aq-clear-btn" onClick={clearFilters}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        <span>Clear all filters</span>
                    </button>
                )}
                <button
                    type="button"
                    className={classnames('aq-shelf-toggle', { open: shelfOpen })}
                    onClick={() => setShelfOpen((v) => !v)}
                >
                    <span>Filters</span>
                    <svg className="aq-shelf-chevron" viewBox="0 0 24 16" fill="none"
                         stroke="currentColor" strokeWidth="2.5"
                         strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2 2 12 13 22 2" />
                    </svg>
                </button>
                <div className="aq-search-wrap">
                    <SearchIcon className="aq-search-ic" />
                    <input
                        className="aq-search"
                        placeholder="Search quotes…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* ── Collapsible filter shelf ── */}
            {shelfOpen && (
                <div className="aq-shelf" data-testid="aq-shelf">
                    <div className="aq-shelf-row">
                        <AqDropdown
                            label="Status" value={filters.status} options={STATUS_OPTIONS}
                            onChange={setFilter('status')}
                            isOpen={openDropdown === 'status'} onToggle={toggleDropdown('status')}
                        />
                        <AqDropdown
                            label="Deal Type" value={filters.dealType} options={DEAL_TYPE_OPTIONS}
                            onChange={setFilter('dealType')}
                            isOpen={openDropdown === 'dealType'} onToggle={toggleDropdown('dealType')}
                        />
                        <AqDropdown
                            label="Year" value={filters.year} options={yearOptions()}
                            onChange={setFilter('year')}
                            isOpen={openDropdown === 'year'} onToggle={toggleDropdown('year')}
                        />
                        <AqDropdown
                            label="Make" value={filters.make} options={makeOptions}
                            onChange={setFilter('make')} loading={loadingOpts.makes}
                            isOpen={openDropdown === 'make'} onToggle={toggleDropdown('make')}
                        />
                        <AqDropdown
                            label="Model" value={filters.model} options={modelOptions}
                            onChange={setFilter('model')} loading={loadingOpts.models}
                            disabled={!filters.make || filters.make === 'All'}
                            isOpen={openDropdown === 'model'} onToggle={toggleDropdown('model')}
                        />
                        <AqDropdown
                            label="Trim" value={filters.trim} options={trimOptions}
                            onChange={setFilter('trim')} loading={loadingOpts.trims}
                            disabled={!filters.model || filters.model === 'All'}
                            isOpen={openDropdown === 'trim'} onToggle={toggleDropdown('trim')}
                        />
                    </div>
                    <div className="aq-shelf-row">
                        <AqRange
                            label="MONTHLY" min={filters.monthlyMin} max={filters.monthlyMax}
                            onMinChange={setFilter('monthlyMin')} onMaxChange={setFilter('monthlyMax')}
                        />
                        <AqRange
                            label="MSRP" min={filters.msrpMin} max={filters.msrpMax}
                            onMinChange={setFilter('msrpMin')} onMaxChange={setFilter('msrpMax')}
                        />
                    </div>
                </div>
            )}

            <div className="modal-body">
                {/* ── Selected rail ── */}
                <aside className="aq-rail" data-testid="aq-selected-rail">
                    {selectedDeals.length === 0 ? (
                        <div className="aq-rail-empty">Nothing selected yet.</div>
                    ) : (
                        <div className="cdv aq-cdv aq-rail-cards">
                            {selectedDeals.map((d) => (
                                <QuoteCard
                                    key={d.id}
                                    quote={toQuoteSummary(d)}
                                    selectable
                                    selected
                                    disabled={pendingIds.includes(d.id)}
                                    onToggle={() => toggle(d.id)}
                                    onSaved={(newDeal) => setDeals((prev) => prev.map((x) => x.id === newDeal.id ? newDeal : x))}
                                />
                            ))}
                        </div>
                    )}
                </aside>
                <div className="aq-divider"><span>‹ Selected</span></div>

                {/* ── Available quotes ── */}
                <div className="aq-grid-area" data-testid="aq-grid" ref={gridAreaRef}>
                    {loadError ? (
                        <div className="aq-message">
                            Could not load your quotes.
                            <button className="btn-ghost" onClick={() => fetchPage(1, debouncedQuery, false)}>Retry</button>
                        </div>
                    ) : loading ? (
                        <div className="aq-message">Loading quotes…</div>
                    ) : deals.length === 0 ? (
                        <div className="aq-message">{emptyText}</div>
                    ) : (
                        <CustomInfiniteScroll
                            className="cdv aq-cdv"
                            useWindow={false}
                            getScrollParent={getScrollParent}
                            loadMore={loadNextPage}
                            hasMore={hasMore}
                        >
                            <div className="quotes-grid">
                                {deals.map((d) => (
                                    <QuoteCard
                                        key={d.id}
                                        quote={toQuoteSummary(d)}
                                        selectable
                                        selected={attachedMap[d.id] !== undefined}
                                        disabled={pendingIds.includes(d.id)}
                                        onToggle={() => toggle(d.id)}
                                        onSaved={(newDeal) => setDeals((prev) => prev.map((x) => x.id === newDeal.id ? newDeal : x))}
                                    />
                                ))}
                            </div>
                        </CustomInfiniteScroll>
                    )}
                </div>
            </div>

        </Dialog>
    )
}
